import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { Activity, Briefcase, Plug, AlertTriangle, FileText } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Topbar } from "@/components/dashboard/Topbar";
import { ClientOnly } from "@/components/dashboard/ClientOnly";
import { GlassPanel } from "@/components/dashboard/GlassPanel";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { tooltipStyle, axisTick, gridStroke } from "@/components/dashboard/chartStyle";
import { useDashboardLive } from "@/lib/dashboard-live";

export const Route = createFileRoute("/activity")({
  head: () => ({ meta: [{ title: "Activity · GENRL" }] }),
  component: ActivityPage,
});

function ActivityPage() {
  const live = useDashboardLive();
  const series = useMemo(
    () => live.weeklyRows.map((d) => ({ t: d.day, jobs: d.jobs })),
    [live.weeklyRows],
  );

  const connectionEvents = useMemo(
    () => live.activityFeed.filter((row) => row.label.toLowerCase().startsWith("connection ")).length,
    [live.activityFeed],
  );

  return (
    <>
      <Topbar title="Activity" subtitle="Unified event stream across jobs, connections, and routing lifecycle." />
      <main className="flex-1 space-y-6 p-6">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <MetricCard label="Recent events" value={live.activityFeed.length} icon={Activity} accent="cyan" />
          <MetricCard label="Jobs completed" value={live.metrics.jobsCompleted.toLocaleString()} icon={Briefcase} accent="mint" />
          <MetricCard label="Failures" value={live.metrics.jobsFailed} icon={AlertTriangle} accent="magenta" hint="terminal job failures" />
          <MetricCard label="Connect events" value={connectionEvents} icon={Plug} accent="amber" />
        </div>

        <GlassPanel title="Weekly throughput" subtitle="Completed + active job activity over the last 7 days">
          <div className="h-64">
            <ClientOnly><ResponsiveContainer>
              <AreaChart data={series} margin={{ left: -10, right: 8 }}>
                <defs>
                  <linearGradient id="jobs" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="oklch(0.62 0.18 245)" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="oklch(0.62 0.18 245)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={gridStroke} vertical={false} />
                <XAxis dataKey="t" tick={axisTick} axisLine={false} tickLine={false} />
                <YAxis tick={axisTick} axisLine={false} tickLine={false} width={32} />
                <Tooltip {...tooltipStyle} />
                <Area type="monotone" dataKey="jobs" stroke="oklch(0.62 0.18 245)" strokeWidth={2} fill="url(#jobs)" name="Jobs" />
              </AreaChart>
            </ResponsiveContainer></ClientOnly>
          </div>
        </GlassPanel>

        <GlassPanel title="Live activity" subtitle="Newest events first">
          {live.activityFeed.length === 0 ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <FileText className="h-4 w-4" />
              No activity yet.
            </div>
          ) : (
            <ol className="relative space-y-3 border-l hairline pl-5">
              {live.activityFeed.map((e, i) => (
                <li key={`${e.time}-${e.label}-${i}`} className="relative">
                  <span className="absolute -left-[27px] top-1 flex h-4 w-4 items-center justify-center rounded-full border hairline bg-[var(--background)]">
                    <Activity className="h-2.5 w-2.5 text-primary" />
                  </span>
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono text-[11px] text-muted-foreground">{e.time}</span>
                    <p className="text-sm">{e.label}</p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </GlassPanel>
      </main>
    </>
  );
}
